/**
 * useTaxiPlanLoader — bridges persisted taxi plans into the editor store.
 *
 * Loads the location's plans; when one exists it loads the baseline (or the
 * selected version) into `taxiEditorStore.draftPlan`; when none exist it seeds an
 * in-memory plan from the synthesized field graph + authored exit points (the
 * user can Save to persist). The draft drives routing whether or not the editor
 * is open, so animations always follow the authored network.
 *
 * A `loadedKey` ref guards against clobbering unsaved edits — the draft is only
 * (re)loaded when the target version id actually changes.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchHangarsWithUnitsForLocation } from '@/lib/api/hangars'
import { CoordinateService } from '@/services/3d/CoordinateService'
import { useTaxiPlans } from '@/hooks/useTaxiPlans'
import type { FieldGraph } from '../data/fieldGraph'
import { buildEmptyPlan, buildSeedPlan, type SeedExit } from '../data/seedTaxiPlan'
import { useTaxiEditorStore } from '../store/taxiEditorStore'
import type { TaxiPlan } from '../data/taxiGraph'

interface Params {
  locationId: string | undefined
  mapCenter: { lat: number; lng: number } | undefined
  /** Optional — when present (event planner), a missing plan is seeded from it;
   *  when absent (layout designer), a missing plan is seeded empty. */
  fieldGraph?: FieldGraph | undefined
}

export function useTaxiPlanLoader({ locationId, mapCenter, fieldGraph }: Params) {
  const plansQ = useTaxiPlans(locationId)
  const [activePlanId, setActivePlanId] = useState<string | null>(null)
  const loadedKey = useRef<string | null>(null)
  const loadPlan = useTaxiEditorStore((s) => s.loadPlan)

  // Authored exit points → seed exit nodes (scene feet).
  const exitsQ = useQuery({
    queryKey: ['taxi-seed-exits', locationId],
    queryFn: () => fetchHangarsWithUnitsForLocation(locationId!),
    enabled: !!locationId,
    staleTime: 60_000,
  })

  // Memoized so the seed arrays keep a stable identity across renders — they flow
  // into `effectiveFieldGraph` / plan building, and a fresh array every render
  // would rebuild every movement plan on every tick.
  const seedExits: SeedExit[] = useMemo(() => {
    if (!exitsQ.data || !mapCenter) return []
    const out: SeedExit[] = []
    for (const a of exitsQ.data) {
      const isExit = a.type === 'exit_point' || a.area_category === 'exit'
      if (!isExit) continue
      const lat = Number(a.latitude)
      const lng = Number(a.longitude)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
      const [x, , z] = CoordinateService.lngLatToScenePosition([lng, lat], [mapCenter.lng, mapCenter.lat])
      out.push({
        name: a.area_code ?? a.name,
        pos: [x, z],
        headingDeg: a.exit_heading != null ? Number(a.exit_heading) : null,
        throughputPerSlot: a.exit_throughput != null ? Number(a.exit_throughput) : null,
      })
    }
    return out
  }, [exitsQ.data, mapCenter])

  // Authored fuel depot → scene feet. Anchors fuel-truck dispatch on real
  // pavement instead of the synthetic bbox-offset position (off-field). First
  // placed fuel_depot area wins.
  const seedFuelDepot: [number, number] | null = useMemo(() => {
    if (!exitsQ.data || !mapCenter) return null
    for (const a of exitsQ.data) {
      if (a.type !== 'fuel_depot' && a.area_category !== 'fuel_depot') continue
      const lat = Number(a.latitude)
      const lng = Number(a.longitude)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
      const [x, , z] = CoordinateService.lngLatToScenePosition([lng, lat], [mapCenter.lng, mapCenter.lat])
      return [x, z]
    }
    return null
  }, [exitsQ.data, mapCenter])

  useEffect(() => {
    if (!locationId || !plansQ.data) return
    const plans = plansQ.data

    // Determine the target version.
    let target: TaxiPlan | null = null
    let key: string
    if (plans.length > 0) {
      target = (activePlanId && plans.find((p) => p.id === activePlanId)) || plans.find((p) => p.isBaseline) || plans[0]
      key = target.id
    } else if (fieldGraph && fieldGraph.gates.length > 0) {
      // No persisted plan, but a field graph exists (event planner) — seed from it.
      key = 'seed'
      target = buildSeedPlan(locationId, fieldGraph, seedExits, 'Baseline')
    } else if (fieldGraph === undefined) {
      // Layout designer (no field graph) — seed an empty plan to author from scratch.
      key = 'seed-empty'
      target = buildEmptyPlan(locationId, 'Baseline')
    } else {
      // Field graph provided but not ready yet (no gates) — wait.
      return
    }

    if (loadedKey.current === key) return
    loadedKey.current = key
    loadPlan(target)
    if (plans.length > 0 && !activePlanId) setActivePlanId(target.id)
    // seedExits/fieldGraph intentionally omitted: seed runs once, guarded by loadedKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, plansQ.data, activePlanId, fieldGraph, loadPlan])

  return {
    plans: plansQ.data ?? [],
    activePlanId,
    setActivePlanId,
    /** Authored exit anchors (scene feet) — fed to `buildPlanFromAeroway` for marked handoff waypoints. */
    seedExits,
    /** Authored fuel depot (scene feet) — overrides the synthetic depot in the field graph. */
    seedFuelDepot,
  }
}
